<!-- eslint-disable max-len -->
<template>
  <v-app id="reckon-app">
    <v-system-bar v-if="draftModeEnabled" app color="amber darken-3" dark height="32">
      <strong class="draft-mode-label">DRAFT MODE</strong>
      <v-spacer />
      <v-btn text x-small @click="viewAsPublic">View as Public</v-btn>
    </v-system-bar>
    <v-app-bar
      app
      color="black"
      dark
    >
      <div class="align-center">
        <router-link :to="{path: '/'}">
          <v-img
            alt="Vuetify Name"
            class="shrink mt-1"
            contain
            min-width="100"
            :src="logoImage | mediaSrc"
            width="150"
          />
        </router-link>
      </div>
      <v-spacer></v-spacer>
      <div class="d-none d-md-block">
        <v-btn
          :to="{name: 'contact'}"
          text
          style="font-size: 1.5rem;"
        >
          <span  style="font-family: Oswald, serif;">Contact Us</span>
        </v-btn>
      </div>
      <v-menu v-if="authReady && isAuthenticated" offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            aria-label="Open account menu"
            class="ml-2"
            icon
            v-bind="attrs"
            v-on="on"
          >
            <v-avatar v-if="currentUser.photoURL" size="32">
              <v-img :src="currentUser.photoURL" :alt="`${accountName} profile photo`" />
            </v-avatar>
            <v-icon v-else>mdi-account-circle</v-icon>
          </v-btn>
        </template>

        <v-list dark>
          <v-list-item :to="loginDestination">
            <v-list-item-content>
              <v-list-item-title>{{ accountName }}</v-list-item-title>
              <v-list-item-subtitle>{{ currentUser.email }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="isAdmin" :to="{ name: 'vera-knowledge-admin' }">
            <v-list-item-icon>
              <v-icon>mdi-robot-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Vera knowledge</v-list-item-title>
          </v-list-item>
          <template v-if="isAuthorizedReckonUser">
            <v-divider />
            <v-list-item>
              <v-list-item-content>
                <v-switch
                  :input-value="draftModeEnabled"
                  color="amber"
                  dense
                  hide-details
                  label="Draft Mode"
                  @change="setDraftMode"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
          <v-list-item v-else>
            <v-list-item-content>
              <v-list-item-subtitle>Draft Mode requires an authorized Reckon account.</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
          <v-list-item @click="signOut">
            <v-list-item-icon>
              <v-icon>mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Sign out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-else-if="authReady"
        aria-label="Team Preview sign in"
        class="ml-2"
        text
        :to="loginDestination"
      >
        <v-icon left>mdi-account-circle</v-icon><span class="d-none d-sm-inline">Team Preview</span>
      </v-btn>
      <v-icon @click="openNavMenu" class="mt-2 ml-3">mdi-menu</v-icon>
    </v-app-bar>
    <v-main id="main-container">

      <v-overlay :value="overlay" :opacity=".8"></v-overlay>
    <div class="main-container">

      <router-view/>
      </div>
    </v-main>
    <nav
      v-if="navMenu"
      class="nav-menu"
      aria-label="Primary navigation"
      v-click-outside="closeNavMenu"
    >
      <router-link
        v-for="item in menuItems"
        :key="item.title"
        class="link px-0"
        :class="{ 'current-route': isCurrentRoute(item.routeName) }"
        :to="item.route"
        :aria-current="isCurrentRoute(item.routeName) ? 'page' : null"
        @click.native="closeNavMenu"
      >
        <div class="py-0 content">
          <div class="section-title">{{ item.title }}</div>
          <div class="subtitle">{{ item.subtitle }}</div>
        </div>
      </router-link>
    </nav>
    <AIAssistant />
    <!-- <v-navigation-drawer right temporary absolute v-model="navMenu" class="nav-menu px-6">

    </v-navigation-drawer> -->
  </v-app>
</template>

<script>
import AIAssistant from './components/AIAssistant.vue';
import { isReckonAdmin } from './lib/admin/permissions';
import { mediaRef } from './services/mediaAssets';

export default {
  name: 'App',
  components: {
    AIAssistant,
  },
  data: () => ({
    logoImage: mediaRef('reckon_logo.png'),
    navMenu: null,
    overlay: false,
    menuItems: [
      {
        title: 'HOME',
        subtitle: 'Back to the Start',
        route: { name: 'home' },
        routeName: 'home',
      },
      {
        title: 'WE RECKON',
        subtitle: 'What We Believe',
        route: { name: 'beliefs' },
        routeName: 'beliefs',
      },
      {
        title: 'THE RECKONING',
        subtitle: 'Our Story',
        route: { name: 'beginnings' },
        routeName: 'beginnings',
      },
      {
        title: 'THE RECKON CREW',
        subtitle: 'Meet the Heroes',
        route: { name: 'reckon-way-people' },
        routeName: 'reckon-way-people',
      },
      {
        title: 'THE RECKON WAY',
        subtitle: 'Life at Reckon',
        route: { name: 'reckon-way' },
        routeName: 'reckon-way',
      },
      {
        title: 'OUR SUPER POWERS',
        subtitle: 'How We Solve Problems',
        route: { name: 'capabilities' },
        routeName: 'capabilities',
      },
      {
        title: 'RECKON IT ALL',
        subtitle: 'Why Join Reckon',
        route: { name: 'benefits' },
        routeName: 'benefits',
      },
      {
        title: 'SEND THE SIGNAL',
        subtitle: 'Start a Conversation',
        route: { name: 'contact' },
        routeName: 'contact',
      },
    ],
  }),
  methods: {
    closeNavMenu() {
      this.navMenu = false;
      this.overlay = false;
      document.getElementById('reckon-app').style.overflow = '';
    },
    openNavMenu() {
      this.navMenu = !this.navMenu;
      this.overlay = this.navMenu;
      document.getElementById('reckon-app').style.overflow = this.navMenu ? 'hidden' : '';
    },
    isCurrentRoute(routeName) {
      return this.$route.name === routeName;
    },
    async signOut() {
      try {
        await this.$store.dispatch('signOut');
      } catch (error) {
        if (this.$route.name !== 'login') {
          this.$router.push({ name: 'login' });
        }
      }
    },
    async setDraftMode(enabled) {
      await this.$store.dispatch('setDraftMode', enabled);
    },
    viewAsPublic() {
      this.$store.dispatch('setDraftMode', false);
    },
  },
  computed: {
    authReady() {
      return this.$store.getters.authReady;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    isAdmin() {
      return isReckonAdmin(this.currentUser);
    },
    isAuthorizedReckonUser() {
      return this.$store.getters.isAuthorizedReckonUser;
    },
    draftModeEnabled() {
      return this.$store.getters.draftModeEnabled;
    },
    accountName() {
      return this.currentUser.displayName || this.currentUser.email || 'Account';
    },
    loginDestination() {
      if (this.$route.name === 'login') return { name: 'login' };

      return {
        name: 'login',
        query: { redirect: this.$route.fullPath },
      };
    },
  },
};
</script>

<style lang="scss">
@import './styles/variables.scss';

.v-application {
  font-family: $body-font-family !important;
  .block-quote {
    font-family: "Comic Neue", cursive !important;
  }
}

.v-main {
  z-index: 0;
  overflow: hidden;
}

.theme--dark.v-application {
  background-color: black;
  background-size: cover;

}
.nav-menu {
  color: white;
  position: fixed;
  top: 56px;
  right: 0;
  bottom: 0;
  z-index: 5;
  background-color: black;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: env(safe-area-inset-bottom);
  -webkit-overflow-scrolling: touch;
  cursor: pointer;
  width: 20rem;
  max-width: 100vw;
}

@media (min-width: 960px) {
  .nav-menu {
    top: 64px;
  }
}

.section-title {
  padding-left: 20px;
  padding-right: 20px;
  font-size: 1.8rem;
  font-family: "Oswald", serif !important;
  padding-top: 24px;
}

.subtitle {
  padding-left: 20px;
  padding-bottom: 20px;
  font-size: .8rem;
  color: rgb(192, 30, 30) !important;
  font-family: "Oswald", serif;
}

.v-application a {
  color: white !important;
}

.v-list-item--active {
  background: unset;
}

.text-section {
  padding: 40px;
  border-radius: 20px;
  border: 1px solid #333;
}

.link {
  border-bottom: 1px solid #222;
  display: block;
  text-decoration: none;
}

.link:hover {
  background-color: #111;
}

.link:active {
  background-color: #111;
}

.link:focus-visible {
  outline: 2px solid white;
  outline-offset: -2px;
}

.current-route {
  background-color: #222;
  border-left: 5px solid red;
}

.main-container {
    margin-left: auto;
    margin-right: auto;
    margin-top: 0px;
    min-height: 100vh;
}
.draft-mode-label { font-family: "Oswald", sans-serif; letter-spacing: .08em; }
</style>
